import React, { useEffect, useMemo, useState } from "react";
import { toast } from "../../../components/AppToast";
import DialogBox from "../../../components/DialogBox";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import OrganizationDetailsTab from "./OrganizationDetailsTab";
import CustomerListTab from "./CustomerListTab";
import CustomerOnboardingForm from "./CustomerOnboardingForm";
import { customerInitial, normalizeCustomerRecord } from "./customerModel";
import { attachHierarchyMeta, mergeOrgRowIntoFlat } from "./orgRemoteMappers";

export default function OrganizationWorkspace({
  orgFlat,
  setOrgFlat,
  tenantId,
  auditActor,
  maxHierarchyLevel,
  mode,
  workspaceOrgId,
  setWorkspaceOrgId,
  onClose,
  loadOrganizationDetail,
  refreshCustomersForOrg,
  verifyOrganizationCodeAvailable,
  persistOrganization,
  persistCustomer,
  persistDeleteCustomer,
}) {
  const [activeTab, setActiveTab] = useState("details");
  const [customerIndex, setCustomerIndex] = useState(null);
  const [formKey, setFormKey] = useState(0);
  const [pendingDeleteCustomerIdx, setPendingDeleteCustomerIdx] = useState(null);

  const org = useMemo(() => (workspaceOrgId ? orgFlat.find((o) => o.id === workspaceOrgId) : null), [orgFlat, workspaceOrgId]);

  useEffect(() => {
    if (!loadOrganizationDetail || !workspaceOrgId || mode !== "edit") return undefined;
    let cancelled = false;
    (async () => {
      try {
        const row = await loadOrganizationDetail(workspaceOrgId);
        if (!cancelled && row) {
          setOrgFlat((prev) => attachHierarchyMeta(mergeOrgRowIntoFlat(prev, row)));
        }
      } catch (err) {
        if (!cancelled) {
          const msg = typeof err === "string" ? err : err?.message || "Could not load organization.";
          toast.error(String(msg));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadOrganizationDetail, workspaceOrgId, mode, setOrgFlat]);

  useEffect(() => {
    if (activeTab !== "customers" || !refreshCustomersForOrg || !workspaceOrgId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        await refreshCustomersForOrg(workspaceOrgId);
      } catch (err) {
        if (!cancelled) {
          const msg = typeof err === "string" ? err : err?.message || "Could not refresh suppliers.";
          toast.error(String(msg));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab, refreshCustomersForOrg, workspaceOrgId]);

  const breadcrumbLabel = mode === "create" && !workspaceOrgId ? "Create organization" : org ? `${org.code} — ${org.name}` : "Organization";

  const customerForForm = useMemo(() => {
    if (!org) return customerInitial();
    const list = org.customers || [];
    if (customerIndex === null) return customerInitial();
    const raw = list[customerIndex];
    return raw ? normalizeCustomerRecord(raw, org.id) : customerInitial();
  }, [org, customerIndex]);

  const handleCustomerSave = async (payload) => {
    if (!workspaceOrgId) {
      toast.error("Save the organization in the first tab before adding suppliers.");
      return;
    }
    try {
      if (persistCustomer) {
        const isNew = customerIndex === null;
        const remote = await persistCustomer({
          organizationId: workspaceOrgId,
          customerIndex,
          isNew,
          payload,
        });
        if (remote?.customer && typeof remote.customer === "object") {
          Object.assign(payload, remote.customer);
        }
      }
      setOrgFlat((prev) =>
        prev.map((row) => {
          if (row.id !== workspaceOrgId) return row;
          const list = [...(row.customers || [])];
          if (customerIndex === null) {
            list.push(payload);
          } else {
            list[customerIndex] = payload;
          }
          return {
            ...row,
            customers: list,
            embeddedCustomer: list[0],
          };
        })
      );
      setActiveTab("customers");
      setCustomerIndex(null);
      setFormKey((k) => k + 1);
    } catch (err) {
      const msg = getApiErrorMessage(err, "Supplier save failed.");
      toast.error(msg);
      throw err;
    }
  };

  const confirmDeleteCustomer = async () => {
    if (pendingDeleteCustomerIdx == null || !workspaceOrgId) return;
    const row = orgFlat.find((o) => o.id === workspaceOrgId);
    const victim = row?.customers?.[pendingDeleteCustomerIdx];
    const customerId = victim?.customerId;
    try {
      if (persistDeleteCustomer && customerId) {
        await persistDeleteCustomer({
          organizationId: workspaceOrgId,
          customerId,
          customerIndex: pendingDeleteCustomerIdx,
        });
      }
      setOrgFlat((prev) =>
        prev.map((r) => {
          if (r.id !== workspaceOrgId) return r;
          const list = [...(r.customers || [])];
          list.splice(pendingDeleteCustomerIdx, 1);
          return {
            ...r,
            customers: list,
            embeddedCustomer: list[0],
          };
        })
      );
      toast.success("Supplier removed.");
    } catch (err) {
      const msg = typeof err === "string" ? err : err?.message || "Delete failed.";
      toast.error(String(msg));
    } finally {
      setPendingDeleteCustomerIdx(null);
    }
  };

  return (
    <div className="org-workspace">
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb small mb-0">
          <li className="breadcrumb-item">
            <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" onClick={onClose}>
              Organization
            </button>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            {breadcrumbLabel}
          </li>
        </ol>
      </nav>

      <ul className="nav nav-tabs org-workspace-tabs border-0 gap-1 mb-0" role="tablist">
        <li className="nav-item" role="presentation">
          <button
            type="button"
            className={`nav-link rounded-top ${activeTab === "details" ? "active" : ""}`}
            onClick={() => setActiveTab("details")}
            role="tab"
            aria-selected={activeTab === "details"}
          >
            <i className="bi bi-building me-1" aria-hidden />
            Organization details
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            type="button"
            className={`nav-link rounded-top ${activeTab === "customers" ? "active" : ""}`}
            onClick={() => {
              if (!workspaceOrgId) {
                toast.info("Save organization details first.");
                return;
              }
              setActiveTab("customers");
            }}
            role="tab"
            aria-selected={activeTab === "customers"}
          >
            <i className="bi bi-people me-1" aria-hidden />
            Supplier list
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            type="button"
            className={`nav-link rounded-top ${activeTab === "customer" ? "active" : ""}`}
            onClick={() => {
              if (!workspaceOrgId) {
                toast.info("Save organization details first.");
                return;
              }
              setActiveTab("customer");
            }}
            role="tab"
            aria-selected={activeTab === "customer"}
          >
            <i className="bi bi-person-plus me-1" aria-hidden />
            Add / edit supplier
          </button>
        </li>
      </ul>

      <div className="card border-0 shadow-sm rounded-top-0 org-workspace-card rounded-3 p-3 p-md-4">
        {activeTab === "details" && (
          <OrganizationDetailsTab
            orgFlat={orgFlat}
            setOrgFlat={setOrgFlat}
            orgId={workspaceOrgId}
            maxHierarchyLevel={maxHierarchyLevel}
            tenantId={tenantId}
            auditActor={auditActor}
            verifyOrganizationCodeAvailable={verifyOrganizationCodeAvailable}
            persistOrganization={persistOrganization}
            onSaved={(id) => setWorkspaceOrgId(id)}
          />
        )}

        {activeTab === "customers" && (
          <CustomerListTab
            org={org}
            onRefreshFromApi={
              refreshCustomersForOrg && workspaceOrgId
                ? () => refreshCustomersForOrg(workspaceOrgId)
                : undefined
            }
            onAddCustomer={() => {
              setCustomerIndex(null);
              setFormKey((k) => k + 1);
              setActiveTab("customer");
            }}
            onEditCustomer={(idx) => {
              setCustomerIndex(idx);
              setFormKey((k) => k + 1);
              setActiveTab("customer");
            }}
            onDeleteCustomer={(idx) => setPendingDeleteCustomerIdx(idx)}
          />
        )}

        {activeTab === "customer" && (
          <CustomerOnboardingForm
            key={`${formKey}-${workspaceOrgId}-${customerIndex ?? "new"}`}
            org={org}
            tenantId={tenantId}
            customer={customerForForm}
            formKey={formKey}
            onSave={handleCustomerSave}
            onCancel={() => {
              setActiveTab(workspaceOrgId ? "customers" : "details");
            }}
          />
        )}
      </div>

      {pendingDeleteCustomerIdx != null && (
        <DialogBox
          modalTitle="Remove supplier"
          modalContent={<p className="mb-0">Remove this supplier from the organization? This cannot be undone from the UI.</p>}
          modalFooter={
            <>
              <button type="button" className="btn btn-sm btn-secondary" onClick={() => setPendingDeleteCustomerIdx(null)}>
                Cancel
              </button>
              <button type="button" className="btn btn-sm btn-danger" onClick={confirmDeleteCustomer}>
                Delete
              </button>
            </>
          }
          onClose={() => setPendingDeleteCustomerIdx(null)}
        />
      )}
    </div>
  );
}
